import { useMemo } from 'react';
import { calculateLockStatesWithParentContext } from '../FlowchartView/utils/dependencyUtils';
import { useWaitGateNow } from '../shared/ConditionsDropdown/utils/useWaitGateNow';
import { applyFilters } from '../shared/filterUtils';
import {
  useFlowViewProps,
  useKanbanViewProps,
  useTableViewProps,
  useViewPropsHandlers,
} from './viewProps';
import { useChainActions } from './useChainActions';

/**
 * Filter tasks based on showNestedCampaigns setting
 * When false, hides tasks that have a nestedSprintId
 */
const filterByNestedCampaigns = (tasks, showNestedCampaigns) => {
  if (showNestedCampaigns) return tasks;
  return tasks.filter(task => !task.nestedSprintId);
};

const excludeMilestones = (taskList) => taskList.filter((t) => t.nodeType !== 'milestone' && t.nodeType !== 'note');

/**
 * useViewProps Hook
 * 
 * Consolidates all the props needed for the three main views (Flow, Kanban, Table).
 * This reduces prop drilling in the main component and makes the view configurations
 * easier to maintain.
 * 
 * @param {Object} options - All the dependencies needed to build view props
 * @returns {Object} - { flowProps, kanbanProps, tableProps, handleAddKanbanColumn }
 */
const useViewProps = ({
  // Refs
  flowchartRef,
  // State
  localSprint,
  setLocalSprint,
  tasks,
  statuses,
  calculatedProgress,
  editingKanbanColumnId,
  setEditingKanbanColumnId,
  detailSidebarTaskId,
  setDetailSidebarTaskId,
  attachmentsModalTaskId,
  setAttachmentsModalTaskId,
  isTemplate,
  settings,
  cardDisplaySettingsPerView,
  swimlaneSetting,
  sortSettingsPerView,
  filtersPerView,
  showArchived,
  showActionableOnly,
  showNestedCampaigns,
  // Hooks
  actions,
  handlers,
  modals,
  nested,
  toolbar,
  handleNestTasks,
  handleCompileMissionsIntoNestedCampaign,
  handleExplodeNestedCampaign,
  isMultiCampaignMode = false,
  handleTrashNestedSprint,
  // Parent callbacks
  onSelectSprint,
  onSprintIconChange,
  onAnySprintIconChange,
  onNestCampaign,
  // HUD visibility settings
  flowViewVisibility = {},
  onFlowViewVisibilityChange,
  hideViewExplainerModal = false,
  onHideViewExplainerModalChange,
  // Table column visibility
  tableColumnVisibility,
  // Table swimlane settings
  tableSwimlane,
  tableSwimlaneContext,
  // Undo/redo
  recordAction,
  wrapLastUndoAction,
  // View navigation (for "Open in view" dropdown)
  currentView,
  customViews,
  activeCustomViewId,
  onViewChange,
  onSelectCustomView,
  // Multi-campaign paste support
  openCampaignPickerForPaste,
  // Deliverables warning modal
  handleSubmitDeliverable,
  // Deliverables editing (for conditions modal)
  handleEditDeliverables,
  handleClearDeliverable,
  // Custom fields (for filtering)
  customFields = [],
  allSprints = [],
  loadFullSprint,
  onTidyToolbarBusyChange,
}) => {
  // Filter tasks based on showNestedCampaigns setting
  const nestedFilteredTasks = useMemo(() => {
    return filterByNestedCampaigns(tasks, showNestedCampaigns);
  }, [tasks, showNestedCampaigns]);

  // Calculate lock states for actionable filtering
  const waitNow = useWaitGateNow(nestedFilteredTasks);
  const flowLockStates = useMemo(() => {
    return calculateLockStatesWithParentContext(
      nestedFilteredTasks,
      nested.nestedSprints || [],
      statuses,
      { currentSprint: localSprint, allSprints, now: waitNow },
    );
  }, [nestedFilteredTasks, nested.nestedSprints, statuses, localSprint, allSprints, waitNow]);

  // Apply normal filters for Flow view
  const flowFilteredTasks = useMemo(() => {
    const flowFilters = filtersPerView?.flow || {};
    
    // Check if any standard array filters are active
    const hasArrayFilters = Object.entries(flowFilters).some(([key, value]) => 
      key !== 'customFields' && Array.isArray(value) && value.length > 0
    );
    
    // Check if any custom field filters are active
    const hasCustomFieldFilters = flowFilters.customFields && 
      Object.values(flowFilters.customFields).some(criteria => 
        criteria && (
          (criteria.values && criteria.values.length > 0) ||
          (criteria.search && criteria.search.length > 0) ||
          (criteria.operator && criteria.value !== undefined && criteria.value !== '')
        )
      );
    
    const hasFilters = hasArrayFilters || hasCustomFieldFilters;
    
    if (!hasFilters && !showActionableOnly) {
      return nestedFilteredTasks;
    }
    
    return applyFilters(
      nestedFilteredTasks,
      flowFilters,
      false,
      flowLockStates,
      nested.nestedSprints || [],
      statuses,
      showActionableOnly,
      customFields
    );
  }, [nestedFilteredTasks, filtersPerView, showActionableOnly, nested.nestedSprints, statuses, flowLockStates, customFields]);

  const nonFlowTasks = useMemo(
    () => excludeMilestones(nestedFilteredTasks),
    [nestedFilteredTasks],
  );

  const neuralFilteredTasks = useMemo(() => {
    const neuralFilters = filtersPerView?.neural || {};

    const hasArrayFilters = Object.entries(neuralFilters).some(([key, value]) =>
      key !== 'customFields' && Array.isArray(value) && value.length > 0);

    const hasCustomFieldFilters = neuralFilters.customFields
      && Object.values(neuralFilters.customFields).some(criteria =>
        criteria && (
          (criteria.values && criteria.values.length > 0)
          || (criteria.search && criteria.search.length > 0)
          || (criteria.operator && criteria.value !== undefined && criteria.value !== '')
        ));

    const hasFilters = hasArrayFilters || hasCustomFieldFilters;

    if (!hasFilters && !showActionableOnly) {
      return nonFlowTasks;
    }

    return applyFilters(
      nonFlowTasks,
      neuralFilters,
      false,
      flowLockStates,
      nested.nestedSprints || [],
      statuses,
      showActionableOnly,
      customFields,
    );
  }, [nonFlowTasks, filtersPerView, showActionableOnly, nested.nestedSprints, statuses, flowLockStates, customFields]);

  // For Kanban/Table — milestones are flow-only
  const filteredTasks = nonFlowTasks;

  // Shared handlers
  const {
    handleDeleteTaskWithNestedCheck,
    handleConfirmDeleteTaskWithNested,
    handleDeleteTasksWithNestedCheck,
    handleIconChangeWithNestedSync,
    handleAddKanbanColumn,
  } = useViewPropsHandlers({
    tasks,
    statuses,
    actions,
    handlers,
    modals,
    nested,
    handleTrashNestedSprint,
    onAnySprintIconChange,
    setEditingKanbanColumnId,
  });

  // Chain actions (for Flow view chain management)
  const chainActions = useChainActions(localSprint, setLocalSprint, recordAction);

  // Flow view props
  const flowProps = useFlowViewProps({
    flowchartRef,
    tasks: flowFilteredTasks,
    statuses,
    localSprint,
    allSprints,
    loadFullSprint,
    calculatedProgress,
    settings,
    cardDisplaySettingsPerView,
    filtersPerView,
    showActionableOnly,
    isTemplate,
    actions,
    handlers,
    modals,
    nested,
    toolbar,
    handleNestTasks,
    handleCompileMissionsIntoNestedCampaign,
    handleExplodeNestedCampaign,
    isMultiCampaignMode,
    handleTrashNestedSprint,
    setDetailSidebarTaskId,
    setAttachmentsModalTaskId,
    onSelectSprint,
    onSprintIconChange,
    onNestCampaign,
    flowViewVisibility,
    onFlowViewVisibilityChange,
    hideViewExplainerModal,
    onHideViewExplainerModalChange,
    handleDeleteTaskWithNestedCheck,
    handleDeleteTasksWithNestedCheck,
    handleIconChangeWithNestedSync,
    chainActions,
    // View navigation
    currentView,
    customViews,
    activeCustomViewId,
    onViewChange,
    onSelectCustomView,
    // Multi-campaign paste support
    openCampaignPickerForPaste,
    // Deliverables warning modal
    onSubmitDeliverable: handleSubmitDeliverable,
    // Deliverables editing (for conditions modal)
    onEditDeliverables: handleEditDeliverables,
    onClearDeliverable: handleClearDeliverable,
    onTidyToolbarBusyChange,
    wrapLastUndoAction,
    setLocalSprint,
  });

  // Kanban view props
  const kanbanProps = useKanbanViewProps({
    tasks: filteredTasks,
    statuses,
    localSprint,
    allSprints,
    setLocalSprint,
    calculatedProgress,
    editingKanbanColumnId,
    setEditingKanbanColumnId,
    setDetailSidebarTaskId,
    setAttachmentsModalTaskId,
    cardDisplaySettingsPerView,
    swimlaneSetting,
    sortSettingsPerView,
    filtersPerView,
    showArchived,
    showActionableOnly,
    isTemplate,
    actions,
    handlers,
    modals,
    nested,
    handleTrashNestedSprint,
    onSprintIconChange,
    handleDeleteTaskWithNestedCheck,
    handleDeleteTasksWithNestedCheck,
    handleIconChangeWithNestedSync,
    // View navigation
    currentView,
    customViews,
    activeCustomViewId,
    onViewChange,
    onSelectCustomView,
    // Deliverables warning modal
    onSubmitDeliverable: handleSubmitDeliverable,
    // Deliverables editing (for conditions modal)
    onEditDeliverables: handleEditDeliverables,
    onClearDeliverable: handleClearDeliverable,
    onSelectSprint,
  });

  // Table view props
  const tableProps = useTableViewProps({
    tasks: filteredTasks,
    statuses,
    localSprint,
    allSprints,
    isTemplate,
    calculatedProgress,
    setDetailSidebarTaskId,
    setAttachmentsModalTaskId,
    sortSettingsPerView,
    filtersPerView,
    showArchived,
    showActionableOnly,
    actions,
    handlers,
    modals,
    nested,
    toolbar,
    flowchartRef,
    handleTrashNestedSprint,
    onSelectSprint,
    onSprintIconChange,
    handleDeleteTaskWithNestedCheck,
    handleDeleteTasksWithNestedCheck,
    handleIconChangeWithNestedSync,
    tableColumnVisibility,
    tableSwimlane,
    tableSwimlaneContext,
    // View navigation
    currentView,
    customViews,
    activeCustomViewId,
    onViewChange,
    onSelectCustomView,
    // Deliverables warning modal
    onSubmitDeliverable: handleSubmitDeliverable,
  });

  return {
    flowProps,
    kanbanProps,
    tableProps,
    handleAddKanbanColumn,
    handleConfirmDeleteTaskWithNested,
    handleDeleteTasksWithNestedCheck,
    // Expose filtered tasks for positioning new nodes relative to visible tasks
    flowFilteredTasks,
    neuralFilteredTasks,
  };
};

export default useViewProps;
